import React, { useCallback, useEffect, useState } from 'react'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm, useController } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import {
  selectIsLoadingCannedReplyCategories,
  selectHasErrorCannedReplyCategories,
  selectIsLoadingCannedReplyVariables,
  selectHasLoadedCannedReplyVariables,
  selectHasErrorCannedReplyVariables,
  selectIsLoadingCannedReplyTemplate,
  selectHasLoadedCannedReplyTemplate,
  selectHasErrorCannedReplyTemplate,
  selectCurrentCannedReplyCategories,
  selectCurrentCannedReplyVariables,
  selectPendingCannedReplyById,
  selectCurrentCannedReplyCategoryById,
} from 'ducks/cannedReplies/selectors'

import {
  doFetchCannedReplyCategoriesV1,
  doFetchCannedReplyVariables,
  doFetchCannedReplyByIdV0Settings,
  doSaveCannedReplyTemplate,
  doSaveCannedReplyTemplateDraft,
  doClearCannedReplyTemplateDraft,
} from 'ducks/cannedReplies/operations'
import { isEmpty, omit } from 'util/objects'
import { buildId } from 'util/globalId'
import { useRhfDirtyHold, useConfirmHoldsCallback } from 'util/dirtyHolds'
import { DRAWER_TYPE_CANNED_REPLY_CATEGORIES_UPDATE } from 'ducks/drawers/types'
import { useDrawer } from 'ducks/drawers/hooks'
import { SETTINGS_CANNED_REPLY_TABLE_ID } from 'ducks/tables/ids'
import { selectQueryParamsQueryId } from 'selectors/location'
import MessageTemplateEditor from 'subapps/settings/components/MessageTemplateEditor'
import CannedReplyAutomaticActions from 'subapps/settings/components/CannedReplyAutomaticActions'
import CannedReplyAddTags from 'subapps/settings/components/CannedReplyAddTags'
import CategorySelection from '../../CannedReplyCategorySelection'
import {
  editorWrapper,
  dropdownContainer,
  topFields,
  fullWidth,
  copy,
} from './styles'

// keys should preferably match the object being stored in the entity store
// that way for updates, you can just spread the entity in reset() and not have to set each property individually
const FORM_SCHEMA = yup.object().shape({
  id: yup.string().nullable(),
  name: yup.string().required('Template name is required'),
  subject: yup.string().nullable(),
  body: yup.string().required('Reply body is required'),
  category: yup.string().nullable(), // it's actually the id that is stored here
  // 1. prepend form schema that are not part of the entity with 'form'
  // 2. don't forget to remove it from object being sent onsubmit
  // 3. used to store the selected category dropdown
  formCategory: yup.object().shape({
    id: yup.string().nullable(),
    name: yup.string().nullable(),
  }),
  attachments: yup.array().of(yup.string()),
  formPendingUploads: yup.array(yup.object()),
})

const noCategorySelectOption = { id: null, name: 'Select category' }

const generateDefaultCategoryValues = category => {
  return {
    category: category?.id,
    formCategory: category?.id
      ? {
          id: category?.id,
          name: category?.name,
        }
      : noCategorySelectOption,
  }
}

const generateDefaultValues = (replyFields, category) => {
  return {
    // defaultValue has to match yup form schema
    id: replyFields?.id || null,
    name: replyFields?.name || '',
    subject: replyFields?.subject || '',
    body: replyFields?.body || '',
    attachments: replyFields?.attachments || [],
    formPendingUploads: replyFields?.formPendingUploads || [],
    automatic_actions: replyFields?.automatic_actions || [],
    ...generateDefaultCategoryValues(category),
  }
}

const formEmptyState = generateDefaultValues()

function getMinHeight(numberOfAttachments = 0) {
  // 477 represents the height of everything that is not the editor
  let spacedHeight = window.innerHeight - 656

  if (numberOfAttachments > 0) {
    // The attachment header and margin
    spacedHeight -= 20
    // Each attachment line item
    spacedHeight -= numberOfAttachments * 48
  }
  return spacedHeight < 100 ? 100 : spacedHeight
}

export default function CannedReplyCreateEditDrawer({
  drawerResourceId: cannedReplyId,
  drawerSetCategoryId,
  open,
  onClose,
  onExit,
  queryId,
  drawerId,
  ...rest
}) {
  const dispatch = useDispatch()
  const [isSaving, setIsSaving] = useState(null)
  const isUpdate = !!cannedReplyId && cannedReplyId !== 'new'

  // not needed for creates but cannot have conditional hooks
  // call anyway, it'll just return null
  const cannedReplyToUpdate = useSelector(state =>
    selectPendingCannedReplyById(state, cannedReplyId)
  )

  const cannedReplyDraftDirtyFieldsExist = !isEmpty(
    cannedReplyToUpdate?.dirtyFields || {}
  )

  useEffect(
    () => {
      // need to check drawer open state before fetch dispatches
      // otherwise the dispatch methods in useEffect below will run immediately
      // after this Drawer component is instantiated via `useDrawer` (before openDrawer is even called)
      // Which is unnecessary double dispatch triggering

      // Won't refetch the canned reply if dirtyFields exists, which means
      // we go to create a new category from this component
      if (!open || !!cannedReplyDraftDirtyFieldsExist) return

      dispatch(
        doFetchCannedReplyCategoriesV1({
          skipLoaded: false,
        })
      )

      dispatch(doFetchCannedReplyVariables())

      if (isUpdate) {
        // save the response from the API in 'pending' store, not 'current' (because it'll immediately be used in this edit screen)
        dispatch(
          doFetchCannedReplyByIdV0Settings(
            buildId('CannedReply', cannedReplyId),
            { targetStore: 'pending' }
          )
        )
      }
    },
    [
      dispatch,
      isUpdate,
      cannedReplyId,
      open,
      drawerSetCategoryId,
      cannedReplyDraftDirtyFieldsExist,
    ]
  )

  const categories = useSelector(selectCurrentCannedReplyCategories)

  const variables = useSelector(selectCurrentCannedReplyVariables)

  const [minHeight, setMinHeight] = useState(
    getMinHeight(cannedReplyToUpdate?.attachments?.length)
  )

  // not needed for creates but cannot have conditional hooks
  // call anyway, it'll just return null
  // update: can also be used to load a category created by a previous drawer: drawerSetCategoryId
  const cannedReplyToUpdateCategory = useSelector(state =>
    selectCurrentCannedReplyCategoryById(
      state,
      drawerSetCategoryId || cannedReplyToUpdate?.category
    )
  )

  const originalCategory = useSelector(state =>
    selectCurrentCannedReplyCategoryById(state, cannedReplyToUpdate?.category)
  )

  const isLoadingCategories = useSelector(selectIsLoadingCannedReplyCategories)
  const hasErrorCategories = useSelector(selectHasErrorCannedReplyCategories)
  const isLoadingVariables = useSelector(selectIsLoadingCannedReplyVariables)
  const hasLoadedVariables = useSelector(selectHasLoadedCannedReplyVariables)
  const hasErrorVariables = useSelector(selectHasErrorCannedReplyVariables)
  const isLoadingCannedReplyForUpdate = useSelector(
    selectIsLoadingCannedReplyTemplate
  )
  const hasLoadedCannedReplyForUpdate = useSelector(
    selectHasLoadedCannedReplyTemplate
  )
  const hasErrorCannedReplyForUpdate = useSelector(
    selectHasErrorCannedReplyTemplate
  )
  const isLoadingCannedReply = isUpdate ? isLoadingCannedReplyForUpdate : false
  const hasLoadedCannedReply = isUpdate ? hasLoadedCannedReplyForUpdate : true
  const hasErrorCannedReply = isUpdate ? hasErrorCannedReplyForUpdate : false

  const isLoading =
    isLoadingCategories || isLoadingVariables || isLoadingCannedReply

  const hasError =
    hasErrorCategories || hasErrorVariables || hasErrorCannedReply

  const noCannedReplyFoundById = isUpdate
    ? hasLoadedCannedReply && !cannedReplyToUpdate
    : false

  const canLoadCannedReplyForUpdate = !isLoading && !noCannedReplyFoundById

  // If access this drawer without through CannedReplyDataTable, need to fetch canned replies after saving
  const shouldFetchCannedReplies = !useSelector(state =>
    selectQueryParamsQueryId(state, {
      targetId: SETTINGS_CANNED_REPLY_TABLE_ID,
    })
  )

  const useFormMethods = useForm({
    mode: 'all',
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: formEmptyState,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState,
    control,
    setValue,
    getValues,
    trigger,
  } = useFormMethods

  const { errors, isValid, isDirty, dirtyFields } = formState

  // We're using useController here for controlled components
  // https://react-hook-form.com/v6/api/#Controller
  // https://react-hook-form.com/get-started#IntegratingControlledInputs
  /* const {
    field: { onChange: onBodyChange, onBlur: onBodyBlur, value: bodyValue },
  } = useController({ name: 'body', control }) */

  const {
    field: {
      onChange: onCategoryChange,
      // not necessary considering this is an optional field
      onBlur: onCategoryBlur,
      // what's being returned in value here is one of:
      // 1. defaultValue from useForm
      // 2. category object returned from loading form fields from a template (see reset() further down)
      // 3. similar to above, value returned from a previous drawer e.g. "+ Create New Category"
      // 4. value returned from onCategoryChange (when the dropdown value is changed)
      // All of which need to match the yup form schema FORM_SCHEMA
      value: { id: selectedCategoryId },
      ref: categoryRef,
    },
  } = useController({
    name: 'formCategory',
    control,
  })

  const onCategorySelect = useCallback(
    (_, category) => {
      if (category) {
        // store the 'selected value in dropdown'
        onCategoryChange({
          id: category.id,
          name: category.name,
        })
        // also set the value of category id in category field (what actually gets saved to entity store)
        setValue('category', category?.id)
      }
    },
    [onCategoryChange, setValue]
  )

  useEffect(
    () => {
      if (!open) return
      if (isUpdate && canLoadCannedReplyForUpdate) {
        // load fields with template values
        reset({
          ...generateDefaultValues(cannedReplyToUpdate, originalCategory),
        })
      }
    },
    // We intentionally exclude dependency props here to ensure that this
    // reset only triggers for the initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canLoadCannedReplyForUpdate, isUpdate, open, drawerSetCategoryId]
  )

  useEffect(
    () => {
      if (drawerSetCategoryId && cannedReplyToUpdateCategory) {
        reset(
          {
            ...generateDefaultValues(cannedReplyToUpdate),
            ...cannedReplyToUpdate?.dirtyFields,
            ...generateDefaultCategoryValues(cannedReplyToUpdateCategory),
          },
          {
            keepDefaultValues: true,
          }
        )
      } else {
        // Handle reset after category edit, but didn't create a new category
        reset(
          {
            ...generateDefaultValues(
              cannedReplyToUpdate,
              cannedReplyToUpdateCategory
            ),
            ...cannedReplyToUpdate?.dirtyFields,
          },
          {
            keepDefaultValues: true,
          }
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drawerSetCategoryId, cannedReplyToUpdateCategory]
  )

  const { releaseHold, holdKey } = useRhfDirtyHold(
    drawerId,
    control,
    `Are you sure? You have unsaved ${app.t(
      'canned_reply'
    )} changes that will be lost.`
  )

  const beforeCloseDrawer = useCallback(
    () => {
      dispatch(doClearCannedReplyTemplateDraft(cannedReplyId))
      reset(formEmptyState)
    },
    [dispatch, cannedReplyId, reset]
  )

  const handleOnClose = useConfirmHoldsCallback(
    holdKey,
    () => {
      beforeCloseDrawer()
      onClose()
    },
    [beforeCloseDrawer, onClose]
  )

  const handleOnExit = useConfirmHoldsCallback(
    null,
    () => {
      beforeCloseDrawer()
      onExit()
    },
    [beforeCloseDrawer, onExit]
  )

  const onSubmit = useCallback(
    async data => {
      // remove 'form' prefixed fields otherwise it'll get stored in entity store and maybe sent to api
      const sanitizedData = omit(['formCategory', 'formPendingUploads'], data)
      const { formPendingUploads: uploadAttachments } = data

      setIsSaving(true)
      await dispatch(
        doSaveCannedReplyTemplate(cannedReplyId, sanitizedData, {
          uploadAttachments,
          queryId,
          shouldFetchCannedReplies,
        })
      )
      setIsSaving(false)
      releaseHold()
      handleOnClose()
    },
    [
      dispatch,
      handleOnClose,
      cannedReplyId,
      queryId,
      releaseHold,
      shouldFetchCannedReplies,
    ]
  )

  const { openDrawer: openEditDrawer } = useDrawer(
    DRAWER_TYPE_CANNED_REPLY_CATEGORIES_UPDATE
  )

  const handleOnCreateCategory = useCallback(
    () => {
      const formValues = getValues()
      const dirtyFieldListWithTrueValues = Object.keys(dirtyFields).filter(
        key => dirtyFields[key] === true
      )

      dispatch(
        doSaveCannedReplyTemplateDraft(cannedReplyId, {
          dirtyFields: dirtyFieldListWithTrueValues.reduce(
            (accumulator, currentField) => {
              return {
                ...accumulator,
                [currentField]: formValues[currentField],
              }
            },
            {}
          ),
        })
      )
      releaseHold()
      openEditDrawer('new', {
        additionalProps: {
          queryId,
        },
      })
    },
    [
      dispatch,
      openEditDrawer,
      cannedReplyId,
      getValues,
      queryId,
      releaseHold,
      dirtyFields,
    ]
  )

  const DrawerForm = useCallback(
    props => <form onSubmit={handleSubmit(onSubmit)} {...props} />,
    [handleSubmit, onSubmit]
  )

  const handleOnResize = useCallback(
    () => {
      setMinHeight(getMinHeight(cannedReplyToUpdate?.attachments?.length))
    },
    [cannedReplyToUpdate]
  )

  useEffect(
    () => {
      window.addEventListener('resize', handleOnResize)

      return () => window.removeEventListener('resize', handleOnResize)
    },
    [handleOnResize]
  )

  useEffect(
    () => {
      handleOnResize()
    },
    [cannedReplyToUpdate?.attachments?.length, handleOnResize]
  )

  return (
    <FormProvider {...useFormMethods}>
      <Drawer
        {...rest}
        open={open}
        onClose={handleOnExit}
        size="wide"
        title={
          isUpdate
            ? `Edit ${app.t('canned_reply')}`
            : `New ${app.t('canned_reply')}`
        }
        isLoading={isLoading}
        isError={hasError}
        isNoResultFound={noCannedReplyFoundById}
        footer={
          <ModalBtns
            saveBtnDisabled={
              isLoading || hasError || !isValid || !isDirty || isSaving
            }
            saveBtnText={
              <>
                {isSaving && (
                  <span>
                    Saving<AnimatedEllipsis />
                  </span>
                )}
                {!isSaving && isUpdate && 'Save'}
                {!isSaving && !isUpdate && 'Create'}
              </>
            }
            // type="submit" will automatically trigger the form submit event when clicked
            saveBtnHtmlType="submit"
            tertiaryBtnText="Cancel"
            onClickTertiaryBtn={handleOnClose}
          />
        }
        container={DrawerForm}
      >
        <div className="grui pt-10" css={editorWrapper}>
          <div css={topFields} className="grui mb-12">
            <Field
              {...register('name')}
              label="Name*"
              placeholder={`Enter ${app.t('canned_reply')} name...`}
              name="name"
              validateStatus={errors?.name ? 'error' : undefined}
              help={errors?.name?.message}
            />
            <div css={dropdownContainer}>
              <div css={fieldStyles.labelBox}>Category</div>
              <CategorySelection
                categories={categories}
                categoryId={selectedCategoryId}
                onSelectCategory={onCategorySelect}
                css={fullWidth}
                showCreate
                onCreateCategory={handleOnCreateCategory}
                buttonRef={categoryRef}
                onButtonBlur={onCategoryBlur}
              />
            </div>
          </div>

          <MessageTemplateEditor
            id={cannedReplyId}
            type="CannedReply"
            entityType="cannedReply"
            open={open}
            mode="managed"
            showPluginImageUpload
            showPluginSourceEditing
            showAddAttachment
            showPluginVariablesPlaceholder
            pluginVariablesPlaceholderTypes={
              hasLoadedVariables ? variables : undefined
            }
            formInputSubjectPlaceholder="Enter email subject..."
            queryId={queryId}
            minHeight={`${minHeight}px`}
            maxHeight={`${minHeight + 100}px`}
            shouldResetFieldOnReady
            formInputBodyLabel="Response text"
            formInputSubjectLabel="Email subject"
            formInputBodySubtitle={`This subject line is only used when creating a new conversation with this ${app.t(
              'Canned_Reply'
            )}.`}
          />

          <div className="grui mt-12">
            <div css={fieldStyles.labelBox}>Add tags to conversation</div>
            <div css={copy}>
              {`These tags will be added to the conversation when sending this  ${app.t(
                'Canned_Reply'
              )}.`}
            </div>
            <CannedReplyAddTags
              trigger={trigger}
              getValues={getValues}
              setValue={setValue}
            />
          </div>

          <div className="grui mt-12 mb-12">
            <div css={fieldStyles.labelBox}>Add an action</div>
            <div css={copy}>
              {`These actions will be run when sending this ${app.t(
                'Canned_Reply'
              )}.`}
            </div>
            <CannedReplyAutomaticActions
              control={control}
              trigger={trigger}
              setValue={setValue}
            />
          </div>
        </div>
      </Drawer>
    </FormProvider>
  )
}
